import { useState } from "react";
import { Route, Switch, useLocation } from "react-router-dom";

import { sendMessageToParent } from "../utils/postMessage";
import { OnboardingFlow } from "../../Onboarding/components/OnboardingFlow";
import { RouteOnboardingRequirementDetails } from "../../Onboarding/steps/EligibilityRequirements/RouteOnboardingRequirementDetails";
import {
  OnboardingModule,
  OnboardingModuleCreateAccount
} from "../../Onboarding/types";
import { EmbeddedError } from "../components/EmbeddedError";
import { COUNTRY_OPTIONS } from "../../../constants/user";
import { GetMissingFieldsLevelParameter } from "@wingspanhq/wingspan-api-sdk";
import {
  parseAllowedInternationalPayoutMethods,
  parseAllowedUsPayoutMethods
} from "../utils/parseAllowedPayoutMethods";
import { parseAllowedPaymentMethods } from "../utils/parseAllowedPaymentMethods";
import { parseAllowedFundingMethods } from "../utils/parseAllowedFundingMethods";
import { parseAllowedEntityTypes } from "../utils/parseAllowedEntityTypes";
import {
  PATH_EMBEDDED_ONBOARDING,
  routeHelpersEmbeddedOnboardingRequirementDetails
} from "../paths";

type CreateAccountOptions = Required<OnboardingModuleCreateAccount>["options"];

const CREATE_ACCOUNT_CONFIG: Record<string, CreateAccountOptions> = {
  payer: {
    accountTypes: ["Business"],
    verificationLevel: GetMissingFieldsLevelParameter.BANKING,
    show1099Message: false
  },
  payee: {
    accountTypes: ["Individual", "Business"],
    verificationLevel: GetMissingFieldsLevelParameter.TAX,
    show1099Message: true
  }
};

const isValidCountryCode = (code: string) =>
  COUNTRY_OPTIONS.some(option => option.value === code);

const parseCreateAccountModule = (
  accountType: string | null,
  country: string | null,
  allowUSInternationalPerson: string | null,
  allowedEntityTypes: string | null
): OnboardingModule | null => {
  if (!accountType) {
    return null;
  }

  const config = CREATE_ACCOUNT_CONFIG[accountType];

  if (!config) {
    return null;
  }

  if (country && !isValidCountryCode(country)) {
    return null;
  }

  // Entity-type restriction only applies to payees — payers are always
  // Business, so the param is ignored for them. Empty/invalid input falls
  // through to the default payee account types.
  const entityTypes =
    accountType === "payee" && allowedEntityTypes
      ? parseAllowedEntityTypes(allowedEntityTypes)
      : [];

  return {
    type: "create_account",
    options: {
      ...config,
      ...(entityTypes.length > 0 ? { accountTypes: entityTypes } : {}),
      ...(country ? { country } : {}),
      ...(allowUSInternationalPerson === "true"
        ? { allowUSInternationalPerson: true }
        : {})
    }
  };
};

const parseModules = (
  searchParams: URLSearchParams
): OnboardingModule[] | null => {
  const raw = searchParams.get("modules");

  if (!raw) {
    return null;
  }

  const moduleTypes = raw.split(",").filter(Boolean);

  if (moduleTypes.length === 0) {
    return null;
  }

  const modules: OnboardingModule[] = [];

  for (const type of moduleTypes) {
    if (type === "create_account") {
      const mod = parseCreateAccountModule(
        searchParams.get("create_account.accountType"),
        searchParams.get("create_account.country"),
        searchParams.get("create_account.allowUSInternationalPerson"),
        searchParams.get("create_account.allowedEntityTypes")
      );

      if (!mod) {
        return null;
      }

      modules.push(mod);
    } else if (type === "payout_method") {
      const allowedUsMethodsParam = searchParams.get(
        "payout_method.allowedUsMethods"
      );
      const allowedInternationalMethodsParam = searchParams.get(
        "payout_method.allowedInternationalMethods"
      );
      const localCurrencyOnlyParam = searchParams.get(
        "payout_method.localCurrencyOnly"
      );
      const fallbackCurrencyParam = searchParams.get(
        "payout_method.fallbackCurrency"
      );

      modules.push({
        type: "payout_method",
        ...(allowedUsMethodsParam
          ? {
              allowedUsMethods: parseAllowedUsPayoutMethods(
                allowedUsMethodsParam
              )
            }
          : {}),
        ...(allowedInternationalMethodsParam
          ? {
              allowedInternationalMethods:
                parseAllowedInternationalPayoutMethods(
                  allowedInternationalMethodsParam
                )
            }
          : {}),
        ...(localCurrencyOnlyParam === "true"
          ? { localCurrencyOnly: true }
          : {}),
        ...(fallbackCurrencyParam
          ? { fallbackCurrency: fallbackCurrencyParam }
          : {})
      });
    } else if (type === "payment_method") {
      const allowedMethodsParam = searchParams.get(
        "payment_method.allowedMethods"
      );

      modules.push({
        type: "payment_method",
        ...(allowedMethodsParam
          ? { allowedMethods: parseAllowedPaymentMethods(allowedMethodsParam) }
          : {})
      });
    } else if (type === "payroll_funding_method") {
      const allowedMethodsParam = searchParams.get(
        "payroll_funding_method.allowedMethods"
      );

      modules.push({
        type: "payroll_funding_method",
        ...(allowedMethodsParam
          ? { allowedMethods: parseAllowedFundingMethods(allowedMethodsParam) }
          : {})
      });
    } else {
      modules.push({ type } as OnboardingModule);
    }
  }

  return modules;
};

export const RouteOnboarding = () => {
  const location = useLocation();

  const [modules] = useState(() =>
    parseModules(new URLSearchParams(location.search))
  );

  if (!modules) {
    return (
      <EmbeddedError message="Missing or invalid onboarding modules configuration" />
    );
  }

  // Match the SDK's OnboardingCompletePayload contract. The SDK exposes
  // this verbatim to integrators.
  const completePayload = {
    modules: modules.map(m => m.type)
  };

  return (
    <Switch>
      <Route
        component={RouteOnboardingRequirementDetails}
        path={routeHelpersEmbeddedOnboardingRequirementDetails.path}
      />
      <OnboardingFlow
        basePath={PATH_EMBEDDED_ONBOARDING}
        hideHeader
        modules={modules}
        onSuccess={() => sendMessageToParent("complete", completePayload)}
      />
    </Switch>
  );
};
